import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as StableIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface SchoolSummary {
  school__id: number;
  school__name: string;
  school__registration_number: string;
  school__district__name: string;
  school__district__region__name: string;
  avg_student_attendance: number;
  avg_teacher_attendance: number;
  total_student_days: number;
  total_present_days: number;
  total_absent_days: number;
  total_late_days: number;
  total_excused_days: number;
  days_reported: number;
}

interface SchoolReportData {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  overall_stats: {
    total_schools: number;
    period_days: number;
    avg_student_rate: number;
    avg_teacher_rate: number;
  };
  performance_categories: {
    excellent: number;
    good: number;
    needs_improvement: number;
  };
  school_summaries: SchoolSummary[];
}

const SchoolAttendanceReport: React.FC = () => {
  const [reportData, setReportData] = useState<SchoolReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [regions, setRegions] = useState<Array<{ id: number; name: string }>>([]);
  const [districts, setDistricts] = useState<Array<{ id: number; name: string; region__id: number; region__name: string }>>([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchSchoolReport();
  }, [startDate, endDate, selectedRegion, selectedDistrict]);

  const fetchFilterOptions = async () => {
    try {
      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getFilterOptions();
      if (response.data.status === 'success') {
        setRegions(response.data.data.regions || []);
        setDistricts(response.data.data.districts || []);
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const filteredDistricts = selectedRegion
    ? districts.filter((d) => d.region__id?.toString() === selectedRegion || d.region__name === selectedRegion)
    : districts;

  const fetchSchoolReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getSchoolReports({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        district: selectedDistrict || undefined,
      });

      if (response.data.status === 'success') {
        setReportData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load school attendance report');
      }
    } catch (err: any) {
      console.error('School attendance report error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load school attendance report');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  const getPerformanceIcon = (rate: number) => {
    if (rate >= 95) return <TrendingUpIcon color="success" />;
    if (rate >= 85) return <StableIcon color="warning" />;
    return <TrendingDownIcon color="error" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No school attendance data available
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        School Attendance Summary Report
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => newValue && setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => newValue && setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion}
                  label="Region"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.name}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  label="District"
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {filteredDistricts.map((district) => (
                    <MenuItem key={district.id} value={district.name}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={fetchSchoolReport}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Schools
              </Typography>
              <Typography variant="h3" color="primary">
                {reportData.overall_stats.total_schools}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {reportData.period.days} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Student Rate
              </Typography>
              <Typography variant="h3" color={getPerformanceColor(reportData.overall_stats.avg_student_rate)}>
                {reportData.overall_stats.avg_student_rate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Across all schools
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Teacher Rate
              </Typography>
              <Typography variant="h3" color={getPerformanceColor(reportData.overall_stats.avg_teacher_rate)}>
                {reportData.overall_stats.avg_teacher_rate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Across all schools
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Categories
              </Typography>
              <Box>
                <Chip
                  label={`${reportData.performance_categories.excellent} Excellent`}
                  color="success"
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
                <Chip
                  label={`${reportData.performance_categories.good} Good`}
                  color="warning"
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
                <Chip
                  label={`${reportData.performance_categories.needs_improvement} Need Help`}
                  color="error"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* School Summary Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            School Attendance Summary ({reportData.period.start_date} to {reportData.period.end_date})
          </Typography>

          {reportData.school_summaries.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>School Name</TableCell>
                    <TableCell>Registration</TableCell>
                    <TableCell>District</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell align="right">Student Rate</TableCell>
                    <TableCell align="right">Teacher Rate</TableCell>
                    <TableCell align="right">Days Reported</TableCell>
                    <TableCell align="right">Present Days</TableCell>
                    <TableCell align="right">Absent Days</TableCell>
                    <TableCell>Performance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.school_summaries.map((school, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {school.school__name}
                        </Box>
                      </TableCell>
                      <TableCell>{school.school__registration_number}</TableCell>
                      <TableCell>{school.school__district__name}</TableCell>
                      <TableCell>{school.school__district__region__name}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${(school.avg_student_attendance || 0).toFixed(1)}%`}
                          color={getPerformanceColor(school.avg_student_attendance || 0)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${(school.avg_teacher_attendance || 0).toFixed(1)}%`}
                          color={getPerformanceColor(school.avg_teacher_attendance || 0)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{school.days_reported}</TableCell>
                      <TableCell align="right">{school.total_present_days || 0}</TableCell>
                      <TableCell align="right">{school.total_absent_days || 0}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getPerformanceIcon(school.avg_student_attendance || 0)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {(school.avg_student_attendance || 0) >= 95 ? 'Excellent' :
                              (school.avg_student_attendance || 0) >= 85 ? 'Good' : 'Needs Help'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No school attendance data available for the selected period and filters.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolAttendanceReport;